import React, { useEffect, useMemo } from "react";

import {
    Link,
    useLocation,
    useNavigate,
    useNavigation,
} from "@remix-run/react";
import clsx from "clsx";
import {
    ChevronLeft,
    Menu,
    Settings,
    Upload,
    Calendar,
    Shield,
    LogOut,
} from "lucide-react";

import Logo from "~/components/logo";
import { Button } from "~/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { H1, P } from "~/components/ui/typography";
import { GlobalLoading, useLoader } from "~/context/LoaderContext";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useAnonymous } from "~/hooks/use-anonymous";
import { useIsAdmin } from "~/hooks/use-is-admin";
import { handleLogout } from "~/lib/logout";
import { useIsPractitioner } from "~/lib/user";

import DesktopLayout from "./desktop-layout";
import { UserMenu } from "./user-menu";

export const Section = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => <div className={clsx("px-0 md:px-4 py-4", className)}>{children}</div>;

export const ActionSection = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => (
    <div className={clsx("w-full mx-0 md:-ml-5 md:-mr-5", className)}>
        {children}
    </div>
);

interface LayoutProps {
    children?: React.ReactNode;
    hideSidebar?: boolean;
    hideBack?: boolean;
    showLogout?: boolean;
    actions?: React.ReactNode;
    searchBar?: React.ReactNode;
    backNavOverride?: string;
    loading?: boolean;
    title?: React.ReactNode;
    description?: React.ReactNode;
    /* TODO: these are anti-pattern props and should be reworked */
    displayHeader?: React.ReactNode;
    offsetTop?: boolean;
    hideLogo?: boolean;
    narrow?: boolean;
    stickyHeader?: boolean;
    /* Desktop props */
    hideBackgroundImage?: boolean;
    fullWidth?: boolean;
    headerImageUrl?: string;
    /**
     * Optional right sidebar content. Rendered as a right column on lg+ screens.
     */
    rightSidebar?: React.ReactNode;
    /**
     * Optional session-specific menu items for mobile hamburger menu
     */
    sessionMenuItems?: React.ReactNode;
}

const MobileLayout = ({
    children,
    backNavOverride,
    hideBack = false,
    showLogout = false,
    narrow = false,
    actions,
    loading = false,
    offsetTop = false,
    title,
    description,
    hideLogo = false,
    headerImageUrl,
    sessionMenuItems,
}: LayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isRoot =
        location.pathname === "/" || location.pathname === "/landing";
    const navigation = useNavigation();
    const { user } = useSupabaseSession();

    const backNavPath = useMemo(() => {
        if (backNavOverride) {
            try {
                const url = new URL(backNavOverride);
                return url.pathname + url.search;
            } catch {
                return backNavOverride;
            }
        }
        return undefined;
    }, [backNavOverride]);

    const { isAnonymous } = useAnonymous();
    const { data: isAdmin } = useIsAdmin();
    const { isPractitioner } = useIsPractitioner();

    // Mobile menu component
    const MobileMenu = () => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="p-2 relative z-[9999] pointer-events-auto touch-manipulation"
                    style={{ minWidth: "44px", minHeight: "44px" }}
                >
                    <Menu className="size-5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-30 z-[9999]">
                {sessionMenuItems && (
                    <>
                        <div className="px-3 py-2 text-sm font-semibold text-muted-foreground">
                            Session
                        </div>
                        {sessionMenuItems}
                        <DropdownMenuSeparator />
                    </>
                )}
                <DropdownMenuItem asChild>
                    <Link
                        to={
                            isPractitioner
                                ? "/practitioner/sessions"
                                : "/sessions"
                        }
                        className="flex items-center gap-2 px-3 py-2"
                    >
                        <Calendar className="size-4" />
                        Sessions
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                    <Link
                        to="/upload"
                        className="flex items-center gap-2 px-3 py-2"
                    >
                        <Upload className="size-4" />
                        Upload
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                    <Link
                        to="/account"
                        className="flex items-center gap-2 px-3 py-2"
                    >
                        <Settings className="size-4" />
                        Settings
                    </Link>
                </DropdownMenuItem>
                {isAdmin && (
                    <>
                        <DropdownMenuItem asChild>
                            <Link
                                to="/admin"
                                className="flex items-center gap-2 px-3 py-2"
                            >
                                <Shield className="size-4" />
                                Admin
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                    </>
                )}
                <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                >
                    <LogOut className="size-4" />
                    Log out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <div className="flex min-h-screen max-w-none flex-col items-center justify-center bg-gradient-to-b from-rose-300/20 to-orange-500/20">
            <GlobalLoading />
            {/* AIDEV-NOTE: ScrollRestoration removed - already in root.tsx */}
            <div
                id="layout-container"
                className={clsx(
                    "w-full max-w-md min-h-lg bg-white rounded-md overflow-y-auto flex flex-col items-stretch justify-start shadow-2xl shadow-orange-600/20 border-orange-500/30",
                    "h-screen w-screen md:h-auto md:w-auto md:border md:rounded-none min-h-screen min-w-screen max-w-screen max-h-auto",
                )}
                style={{
                    minHeight: "100vh",
                    maxHeight: "auto",
                    minWidth: "100vw",
                    maxWidth: "100vw",
                    backgroundImage: `url('/background-gradient.png')`,
                    backgroundSize: "100vw 200vw",
                    backgroundPosition: "top -30vh",
                    backgroundRepeat: "no-repeat",
                }}
            >
                {headerImageUrl && (
                    <div className="mb-4 h-[300px] w-full">
                        <img
                            src={headerImageUrl}
                            alt="Header background"
                            className="size-full object-cover"
                        />
                    </div>
                )}
                <div
                    className={clsx(
                        "w-full h-full",
                        "px-6 py-12 mt-6 md:p-8 md:mx-auto",
                    )}
                    style={{
                        maxWidth: narrow ? "32rem" : "72rem",
                        paddingTop: offsetTop ? "10vh" : "0",
                    }}
                >
                    <div
                        className={clsx(
                            "flex justify-start w-full px-0 md:pt-8 pb-4 background-white dropshadow",
                        )}
                    >
                        <div className="flex items-center justify-start gap-4">
                            {!hideBack && !isRoot && (
                                <div className="items-center justify-center">
                                    <Button
                                        variant="ghostIcon"
                                        onClick={() =>
                                            backNavPath
                                                ? navigate(backNavPath)
                                                : navigate(-1)
                                        }
                                        className="bg-transparent pl-2 pr-3 text-rose-500 hover:bg-orange-100"
                                        analyticsId="page back"
                                    >
                                        <ChevronLeft className="size-24" />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-1 items-center">
                            {title && !hideLogo ? (
                                <H1 className="mx-auto mb-1 mt-0 text-center text-2xl font-bold tracking-tight text-rose-800">
                                    {title}
                                </H1>
                            ) : !hideLogo ? (
                                <div className="flex flex-col items-center gap-6">
                                    <Logo size={"sm"} />
                                </div>
                            ) : null}
                        </div>
                        <div
                            className={clsx(
                                "space-x-4 flex items-center justify-end",
                                !hideBack && "min-w-[40px]",
                            )}
                        >
                            {isAnonymous && showLogout && (
                                <Link to="/link-account">
                                    <Button
                                        variant="outline"
                                        analyticsId="Link anonymous account"
                                    >
                                        Create an account
                                    </Button>
                                </Link>
                            )}
                            {actions}
                            {!isAnonymous && showLogout && user?.email && (
                                <div className="hidden md:block">
                                    <UserMenu />
                                </div>
                            )}
                            {/* Mobile hamburger menu - only show on mobile */}
                            {!isAnonymous && (
                                <div className="block md:hidden relative z-[9999] pointer-events-auto">
                                    <MobileMenu />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex max-w-none flex-1 flex-col items-center justify-center space-y-8">
                        {description && <P>{description}</P>}
                    </div>
                    <div
                        className={clsx(
                            "flex items-start justify-center gap-8 grow md:p-4 mb-16",
                        )}
                    >
                        {navigation.state === "loading" || loading ? (
                            <></>
                        ) : (
                            <>
                                <div
                                    className="flex min-w-80 flex-1 flex-col items-center justify-center space-y-4"
                                    id="layout-body"
                                >
                                    {children}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* Important: to support SSR hydration, we render both mobile and desktop layouts
   on the server. Then, after the first render, we pick which to keep around (variant)
   */
export default function ResponsiveLayout(props: LayoutProps) {
    const [variant, setVariant] = React.useState<string>("unknown");

    React.useEffect(() => {
        const isNarrow = window.matchMedia("(max-width: 1023px)").matches;
        setVariant(isNarrow ? "mobile" : "desktop");
    }, []);

    if (import.meta.env.MODE === "test") {
        return <DesktopLayout {...props} />;
    }

    if (variant === "mobile" || props.narrow) {
        return <MobileLayout {...props} />;
    }
    if (variant === "desktop") {
        return <DesktopLayout {...props} />;
    }

    // AIDEV-NOTE: Prevent event capture issues by only rendering one layout during hydration
    return (
        <>
            {/* Mobile & tablet view: show Layout, hide on lg+ */}
            <div
                className="block size-full lg:hidden"
                style={{
                    pointerEvents: variant === "desktop" ? "none" : "auto",
                }}
            >
                <MobileLayout {...props} />
            </div>

            {/* Desktop view: hide below lg, show DesktopLayout on lg+ */}
            <div
                className="hidden size-full lg:block"
                style={{
                    pointerEvents: variant === "mobile" ? "none" : "auto",
                }}
            >
                <DesktopLayout {...props} />
            </div>
        </>
    );
}
